// Whiteboard — the annotation inspector: what a selected drawing annotation
// says. Typing shows at once on the board; it is saved when you leave the box
// (or press Enter). Choices save as they are made. Drag it by its title bar
// to wherever it is out of the way — it stays there.

import React, { useEffect, useRef, useState } from 'react';
import { Icon, Tip } from '@modules/database/whiteboard/frontend/ui';
import { GDT_CHARS, SYMBOL_KINDS, gdtGlyph } from '@modules/database/whiteboard/frontend/engine/symbols';
import { opsToSvg } from '@modules/database/whiteboard/frontend/engine/scene';

const MODS = [['', 'None'], ['M', 'Ⓜ Maximum material (MMC)'], ['L', 'Ⓛ Least material (LMC)'], ['P', 'Ⓟ Projected zone'], ['F', 'Ⓕ Free state'], ['T', 'Ⓣ Tangent plane']];
const DMODS = [['', '—'], ['M', 'Ⓜ'], ['L', 'Ⓛ']];

function Glyph({ char, size = 18 }) {
  const g = gdtGlyph(char, 12, 12, 16);
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d={opsToSvg(g.ops)} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      {g.fill.map((f, i) => <path key={i} d={opsToSvg(f)} fill="currentColor" />)}
    </svg>
  );
}

// A text box that previews as you type and saves when you leave it.
function Field({ label, value, onLive, onSave, width, autoFocus, placeholder }) {
  const [v, setV] = useState(value || '');
  const ref = useRef(null);
  useEffect(() => { setV(value || ''); }, [value]);
  useEffect(() => { if (autoFocus) { ref.current?.focus(); ref.current?.select(); } }, [autoFocus]);
  return (
    <label className="wb-sp-field" style={width ? { width } : undefined}>
      <span>{label}</span>
      <input ref={ref} className="wb-input" value={v} placeholder={placeholder}
        onChange={(e) => { setV(e.target.value); onLive(e.target.value); }}
        onBlur={(e) => onSave(e.target.value)}
        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.currentTarget.blur(); } }} />
    </label>
  );
}
function Select({ label, value, options, onSave }) {
  return (
    <label className="wb-sp-field">
      <span>{label}</span>
      <select className="wb-input" value={value || ''} onChange={(e) => onSave(e.target.value)} onKeyDown={(e) => e.stopPropagation()}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
function Check({ label, value, onSave }) {
  return (
    <label className="wb-sp-check">
      <input type="checkbox" checked={!!value} onChange={(e) => onSave(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

// el: the selected annotation; onLive(params) previews, onSave(params) saves.
export default function SymbolPanel({ el, focusFirst, onLive, onSave, onLeader, onClose, pos, onMove, boardRef }) {
  const box = useRef(null);
  const [dragAt, setDragAt] = useState(null);
  // keep it on the board (the board may be smaller than when it was put there)
  const place = (p) => {
    const b = boardRef?.current?.getBoundingClientRect(); const me = box.current?.getBoundingClientRect();
    if (!p || !b || !me) return p;
    return { x: Math.max(4, Math.min(b.width - me.width - 4, p.x)), y: Math.max(4, Math.min(b.height - 60, p.y)) };
  };
  function startDrag(e) {
    if (e.button !== 0 || e.target.closest('button')) return;
    const b = boardRef?.current?.getBoundingClientRect(); const me = box.current?.getBoundingClientRect();
    if (!b || !me) return;
    e.preventDefault();
    const off = { x: e.clientX - me.left, y: e.clientY - me.top };
    let last = null;
    const move = (ev) => { last = place({ x: ev.clientX - b.left - off.x, y: ev.clientY - b.top - off.y }); setDragAt(last); };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      if (last) onMove?.(last);
      setDragAt(null);
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  }
  const at = dragAt || place(pos);
  const where = at ? { left: at.x, top: at.y, right: 'auto', bottom: 'auto' } : undefined;
  const p = el.params || {};
  const live = (k) => (v) => onLive({ ...p, [k]: v });
  const save = (k) => (v) => onSave({ ...p, [k]: v });
  const kind = SYMBOL_KINDS.find((k) => k.id === el.kind);
  let body = null;
  switch (el.kind) {
    case 'dimension':
      body = (
        <>
          <div className="wb-sp-row">
            <Select label="Type" value={p.prefix} onSave={save('prefix')} options={[['', 'Linear'], ['dia', 'Ø Diameter'], ['rad', 'R Radius'], ['sdia', 'SØ Sphere']]} />
            <Field label="Value" value={p.value} onLive={live('value')} onSave={save('value')} autoFocus={focusFirst} width={96} />
          </div>
          <Select label="Tolerance" value={p.tolType} onSave={save('tolType')}
            options={[['none', 'None'], ['sym', '± Symmetric'], ['bilat', 'Bilateral  +x / −y'], ['limit', 'Limits  upper / lower'], ['basic', 'Basic (boxed)']]} />
          {p.tolType === 'sym' && <Field label="± Tolerance" value={p.plus} onLive={live('plus')} onSave={save('plus')} />}
          {(p.tolType === 'bilat' || p.tolType === 'limit') && (
            <div className="wb-sp-row">
              <Field label={p.tolType === 'limit' ? 'Upper limit' : 'Plus (+)'} value={p.plus} onLive={live('plus')} onSave={save('plus')} />
              <Field label={p.tolType === 'limit' ? 'Lower limit' : 'Minus (−)'} value={p.minus} onLive={live('minus')} onSave={save('minus')} />
            </div>
          )}
          <div className="wb-sp-note">Drag the ends onto the feature; drag the middle handle to move the dimension line.</div>
        </>
      );
      break;
    case 'fcf':
      body = (
        <>
          <div className="wb-sp-label">Characteristic</div>
          <div className="wb-sp-chars" role="radiogroup" aria-label="Characteristic">
            {GDT_CHARS.map(([c, l]) => (
              <Tip key={c} label={l} side="top">
                <button type="button" role="radio" aria-checked={p.char === c} aria-label={l} className={`wb-sp-char ${p.char === c ? 'is-active' : ''}`} onClick={() => onSave({ ...p, char: c })}>
                  <Glyph char={c} />
                </button>
              </Tip>
            ))}
          </div>
          <div className="wb-sp-current">{(GDT_CHARS.find(([c]) => c === p.char) || [])[1]}</div>
          <div className="wb-sp-row">
            <Check label="Ø zone" value={p.dia} onSave={save('dia')} />
            <Field label="Tolerance" value={p.tol} onLive={live('tol')} onSave={save('tol')} autoFocus={focusFirst} width={90} />
          </div>
          <Select label="Material modifier" value={p.mod} onSave={save('mod')} options={MODS} />
          <div className="wb-sp-label">Datum references</div>
          <div className="wb-sp-row wb-sp-datums">
            {['d1', 'd2', 'd3'].map((d, i) => (
              <div key={d} className="wb-sp-datum">
                <Field label={['Primary', 'Secondary', 'Tertiary'][i]} value={p[d]} onLive={live(d)} onSave={save(d)} width={66} placeholder="—" />
                <select className="wb-input wb-sp-dmod" value={p[`${d}m`] || ''} aria-label={`${d} modifier`} onChange={(e) => onSave({ ...p, [`${d}m`]: e.target.value })}>
                  {DMODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            ))}
          </div>
        </>
      );
      break;
    case 'datum':
      body = <Field label="Datum letter" value={p.letter} onLive={live('letter')} onSave={save('letter')} autoFocus={focusFirst} width={90} />;
      break;
    case 'finish':
      body = (
        <>
          <Select label="Surface" value={p.finishType} onSave={save('finishType')} options={[['basic', 'Basic symbol'], ['removal', 'Machining required'], ['noremoval', 'Machining not allowed']]} />
          <Field label="Roughness (e.g. Ra 1.6)" value={p.value} onLive={live('value')} onSave={save('value')} autoFocus={focusFirst} />
          <Field label="Method (e.g. ground)" value={p.method} onLive={live('method')} onSave={save('method')} />
        </>
      );
      break;
    case 'weld':
      body = (
        <>
          <div className="wb-sp-row">
            <Select label="Weld" value={p.wtype} onSave={save('wtype')} options={[['fillet', 'Fillet'], ['vgroove', 'V-groove'], ['square', 'Square butt'], ['bevel', 'Bevel']]} />
            <Select label="Side" value={p.side} onSave={save('side')} options={[['arrow', 'Arrow side'], ['other', 'Other side'], ['both', 'Both sides']]} />
          </div>
          <div className="wb-sp-row">
            <Field label="Size" value={p.size} onLive={live('size')} onSave={save('size')} autoFocus={focusFirst} />
            <Field label="Length – pitch" value={p.length} onLive={live('length')} onSave={save('length')} />
          </div>
          <div className="wb-sp-row">
            <Check label="Weld all around" value={p.around} onSave={save('around')} />
            <Check label="Field weld" value={p.field} onSave={save('field')} />
          </div>
          <Field label="Tail (process, e.g. GMAW)" value={p.tail} onLive={live('tail')} onSave={save('tail')} />
        </>
      );
      break;
    case 'balloon':
      body = <Field label="Caption" value={p.num} onLive={live('num')} onSave={save('num')} autoFocus={focusFirst} placeholder="e.g. 1, 12A, P-04" />;
      break;
    default:
      body = <div className="wb-sp-note">A centre mark. Drag a corner to size it.</div>;
  }
  const canLead = el.kind !== 'dimension' && el.kind !== 'center';
  return (
    <div ref={box} className={`wb-sympanel ${dragAt ? 'is-dragging' : ''}`} style={where} onPointerDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <div className="wb-sp-head" onPointerDown={startDrag}>
        <span className="wb-sp-grip" aria-hidden="true"><Icon name="grip" size={14} /></span>
        <Icon name={el.kind} size={18} />
        <span>{kind?.label || 'Annotation'}</span>
        <button type="button" className="wb-btn" aria-label="Close" onClick={onClose}><Icon name="close" size={16} /></button>
      </div>
      <div className="wb-sp-body">
        {body}
        {canLead && (
          <label className="wb-sp-check">
            <input type="checkbox" checked={!!el.leader} onChange={(e) => onLeader(e.target.checked)} />
            <span>Leader {el.kind === 'datum' ? '(with datum triangle)' : el.kind === 'balloon' ? '(with dot)' : '(with arrow)'}</span>
          </label>
        )}
      </div>
    </div>
  );
}
